import { makeChoices, makeTextChoices } from "../../distractors";
import { clean, frac, numStr, simplify, text } from "../../format";
import type { QuestionDraft, Rng, TopicDef } from "../../types";
import { both, fraction, numeric, q } from "../helpers";

/** Fractions whose decimal expansion terminates within 3 places. */
const NICE: ReadonlyArray<readonly [number, number]> = [
  [1, 2],
  [1, 4],
  [3, 4],
  [1, 5],
  [2, 5],
  [3, 5],
  [4, 5],
  [1, 10],
  [3, 10],
  [7, 10],
  [9, 10],
  [1, 8],
  [3, 8],
  [5, 8],
  [7, 8],
  [1, 20],
  [3, 20],
  [7, 20],
  [1, 25],
  [3, 25],
  [6, 25],
];

function tier1(rng: Rng): QuestionDraft {
  const [n, d] = rng.pick(NICE);
  const value = clean(n / d);
  if (rng.chance(0.5)) {
    const choice4 = makeChoices(rng, value, [clean(d / n), clean(n / 10), clean((n + 1) / d)], (r) =>
      clean(r.int(1, 99) / 100),
    );
    return q(
      both(`Zapisz ułamek ${n}/${d} jako ułamek dziesiętny.`, `Write the fraction ${n}/${d} as a decimal.`),
      numeric(value),
      [`Dzielimy licznik przez mianownik: ${n} : ${d}.`, `${n}/${d} = ${numStr(value, "pl")}.`],
      [`Divide the numerator by the denominator: ${n} ÷ ${d}.`, `${n}/${d} = ${numStr(value, "en")}.`],
      { choice4 },
    );
  }
  const others = [frac(d, n), frac(n + 1, d), frac(n, d * 2), frac(n * 2, d + 1)];
  const choice4 = makeTextChoices(rng, frac(n, d), others);
  const places = numStr(value, "en").split(".")[1]?.length ?? 1;
  const pow = 10 ** places;
  const raw = Math.round(value * pow);
  return q(
    both(
      `Zapisz ${numStr(value, "pl")} jako ułamek zwykły (skróć).`,
      `Write ${numStr(value, "en")} as a fraction (simplify).`,
    ),
    fraction(n, d, true),
    [`${numStr(value, "pl")} to ${raw}/${pow}.`, `Skracamy przez ${raw / n}: ${raw}/${pow} = ${n}/${d}.`],
    [`${numStr(value, "en")} equals ${raw}/${pow}.`, `Divide both by ${raw / n}: ${raw}/${pow} = ${n}/${d}.`],
    { choice4 },
  );
}

function tier2(rng: Rng): QuestionDraft {
  const [n, d] = rng.pick(NICE);
  const decimal = clean(rng.int(1, 19) / (rng.chance(0.5) ? 10 : 4));
  const fracValue = clean(n / d);
  const add = rng.chance(0.6) || decimal < fracValue;
  const result = clean(add ? fracValue + decimal : fracValue - decimal);
  const wrong = clean(add ? fracValue - decimal : fracValue + decimal);
  const choice4 = makeChoices(rng, result, [wrong, clean(result + 0.1), clean(n + decimal)], (r) =>
    clean(r.int(1, 30) / 10),
  );
  const opPl = add ? "+" : "−";
  return q(
    both(
      `Oblicz: ${n}/${d} ${opPl} ${numStr(decimal, "pl")} = ?`,
      `Calculate: ${n}/${d} ${opPl} ${numStr(decimal, "en")} = ?`,
    ),
    numeric(result),
    [
      `Zamieniamy ułamek na dziesiętny: ${n}/${d} = ${numStr(fracValue, "pl")}.`,
      `${numStr(fracValue, "pl")} ${opPl} ${numStr(decimal, "pl")} = ${numStr(result, "pl")}.`,
    ],
    [
      `Convert the fraction to a decimal: ${n}/${d} = ${numStr(fracValue, "en")}.`,
      `${numStr(fracValue, "en")} ${opPl} ${numStr(decimal, "en")} = ${numStr(result, "en")}.`,
    ],
    { choice4 },
  );
}

function tier3(rng: Rng): QuestionDraft {
  const [n, d] = rng.pick(NICE);
  const k = rng.int(1, 9);
  const decimal = clean(k / (rng.chance(0.5) ? 10 : 2));
  const multiply = rng.chance(0.6);
  if (multiply) {
    const result = clean((n / d) * decimal);
    const choice4 = makeChoices(
      rng,
      result,
      [clean(n * decimal), clean(result * 10), clean(result / 2)],
      (r) => clean(r.int(1, 40) / 10),
    );
    return q(
      both(
        `Oblicz: ${n}/${d} · ${numStr(decimal, "pl")} = ?`,
        `Calculate: ${n}/${d} × ${numStr(decimal, "en")} = ?`,
      ),
      numeric(result),
      [
        `${n}/${d} = ${numStr(clean(n / d), "pl")}.`,
        `${numStr(clean(n / d), "pl")} · ${numStr(decimal, "pl")} = ${numStr(result, "pl")}.`,
      ],
      [
        `${n}/${d} = ${numStr(clean(n / d), "en")}.`,
        `${numStr(clean(n / d), "en")} × ${numStr(decimal, "en")} = ${numStr(result, "en")}.`,
      ],
      { choice4 },
    );
  }
  // decimal ÷ fraction, answer as a fraction or integer
  const dividend = clean(rng.int(1, 9) / 10);
  const rawNum = Math.round(dividend * 10) * d;
  const rawDen = 10 * n;
  const { num, den } = simplify(rawNum, rawDen);
  const answer = den === 1 ? numeric(num) : fraction(num, den, true);
  const choice4 =
    den === 1
      ? makeChoices(rng, num, [num * 2, Math.max(1, num - 1), num + 1], (r) => r.int(1, 12))
      : makeTextChoices(rng, frac(num, den), [
          frac(den, num),
          frac(num, den * 2),
          frac(num + 1, den),
          frac(num, den + 1),
        ]);
  const hintPl = den === 1 ? "" : " Wynik podaj jako ułamek.";
  const hintEn = den === 1 ? "" : " Give the result as a fraction.";
  return q(
    both(
      `Oblicz: ${numStr(dividend, "pl")} : ${n}/${d} = ?${hintPl}`,
      `Calculate: ${numStr(dividend, "en")} ÷ ${n}/${d} = ?${hintEn}`,
    ),
    answer,
    [
      `Dzielenie przez ułamek to mnożenie przez odwrotność: ${numStr(dividend, "pl")} · ${d}/${n}.`,
      `${numStr(dividend, "pl")} = ${Math.round(dividend * 10)}/10, więc ${Math.round(dividend * 10)}/10 · ${d}/${n} = ${rawNum}/${rawDen} = ${den === 1 ? num : `${num}/${den}`}.`,
    ],
    [
      `Dividing by a fraction is multiplying by its reciprocal: ${numStr(dividend, "en")} × ${d}/${n}.`,
      `${numStr(dividend, "en")} = ${Math.round(dividend * 10)}/10, so ${Math.round(dividend * 10)}/10 × ${d}/${n} = ${rawNum}/${rawDen} = ${den === 1 ? num : `${num}/${den}`}.`,
    ],
    { choice4 },
  );
}

export const fractionsDecimals: TopicDef = {
  id: "sp6.fractions_decimals",
  level: "sp6",
  name: text("Ułamki zwykłe i dziesiętne", "Fractions and decimals"),
  modes: ["numeric", "fraction", "choice4"],
  generate: (rng, tier) => (tier === 1 ? tier1(rng) : tier === 2 ? tier2(rng) : tier3(rng)),
};
